import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    IconButton,
    InputAdornment,
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Save as SaveIcon,
    CheckCircle,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    SmartToy as LLMIcon,
    Edit as EditIcon,
    Cancel as CancelIcon,
    ContentCopy as CopyIcon,
    AccountBalanceWallet as CreditsIcon
} from '@mui/icons-material';
import api from '../services/api';
import ollamaIcon from '../assets/ollama.png';
import chatGptIcon from '../assets/chatgpt.jpg';
import openRouterIcon from '../assets/openrouter.jpeg';
import googleAiIcon from '../assets/googleai.svg';
import groqIcon from '../assets/groq.svg';

// Providers configured by a base URL rather than an API key.
const URL_PROVIDERS = ['ollama', 'mlx'];
const isUrlProvider = (provider) => URL_PROVIDERS.includes(provider);

const LLMsManagement = ({ readOnly = false }) => {
    const [configs, setConfigs] = useState({
        openai: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        ollama: { url: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        mlx: { url: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        openrouter: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        googleaistudio: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        groq: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
        claude: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, provider: null, newValue: '', fieldName: '' });
    const [creditsDialog, setCreditsDialog] = useState({ open: false, loading: false, data: null, error: null });

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async (retryCount = 0) => {
        try {
            setLoading(true);
            // Longer timeout for initial load (15 seconds) to handle backend startup
            const timeoutMs = retryCount === 0 ? 15000 : 5000;
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
            );

            if (readOnly) {
                // Non-admin: fetch status only (no keys exposed)
                const response = await Promise.race([
                    api.get('/llm-status'),
                    timeoutPromise
                ]);
                const data = response.data;
                const providers = ['openai', 'ollama', 'mlx', 'openrouter', 'googleaistudio', 'groq', 'claude'];
                const newConfigs = {};
                providers.forEach(p => {
                    newConfigs[p] = {
                        apiKey: '',
                        url: '',
                        showKey: false,
                        status: data[p]?.configured ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: '',
                        viewing: false
                    };
                });
                setConfigs(newConfigs);
            } else {
                // Admin: fetch full config with keys
                const response = await Promise.race([
                    api.get('/admin/llm-config'),
                    timeoutPromise
                ]);
                const data = response.data;

                setConfigs({
                    openai: {
                        apiKey: data.openai_api_key || '',
                        showKey: false,
                        status: data.openai_api_key ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.openai_api_key || '',
                        viewing: false
                    },
                    ollama: {
                        url: data.ollama_url || '',
                        showKey: false,
                        status: data.ollama_url ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.ollama_url || '',
                        viewing: false
                    },
                    mlx: {
                        url: data.mlx_url || '',
                        showKey: false,
                        status: data.mlx_url ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.mlx_url || '',
                        viewing: false
                    },
                    openrouter: {
                        apiKey: data.openrouter_api_key || '',
                        showKey: false,
                        status: data.openrouter_api_key ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.openrouter_api_key || '',
                        viewing: false
                    },
                    googleaistudio: {
                        apiKey: data.google_ai_studio_api_key || '',
                        showKey: false,
                        status: data.google_ai_studio_api_key ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.google_ai_studio_api_key || '',
                        viewing: false
                    },
                    groq: {
                        apiKey: data.groq_api_key || '',
                        showKey: false,
                        status: data.groq_api_key ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.groq_api_key || '',
                        viewing: false
                    },
                    claude: {
                        apiKey: data.claude_api_key || '',
                        showKey: false,
                        status: data.claude_api_key ? 'configured' : 'not_configured',
                        testing: false,
                        editing: false,
                        originalValue: data.claude_api_key || '',
                        viewing: false
                    }
                });
            }
        } catch (err) {
            // Retry logic: retry once after 2 seconds if first attempt fails
            if (retryCount === 0 && (err.message?.includes('timeout') || err.message?.includes('Request timeout') || err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK')) {
                console.log('Initial fetch failed, retrying in 2 seconds...');
                setTimeout(() => {
                    fetchConfigs(1);
                }, 2000);
                return;
            }

            const errorMessage = err.message?.includes('timeout') || err.message?.includes('Request timeout')
                ? 'LLM configuration service is not responding. Please check the backend connection and try refreshing the page.'
                : err.response?.status === 401
                ? 'Authentication required. Please log in again.'
                : err.response?.status === 403
                ? 'Access denied. Admin privileges required.'
                : 'Failed to load LLM configurations';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            // Set default empty configs to allow page to display
            setConfigs({
                    openai: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
                    ollama: { url: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
                    mlx: { url: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
                    openrouter: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
                    googleaistudio: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false },
                    groq: { apiKey: '', showKey: false, status: 'unknown', testing: false, editing: false, originalValue: '', viewing: false }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = (provider) => {
        const config = configs[provider];
        const currentValue = isUrlProvider(provider) ? config.url : config.apiKey;
        const originalValue = config.originalValue;
        const fieldName = isUrlProvider(provider) ? 'URL' : 'API Key';
        
        // Check if value has changed
        if (currentValue === originalValue) {
            // No changes, just exit edit mode
            setConfigs(prev => ({
                ...prev,
                [provider]: {
                    ...prev[provider],
                    editing: false,
                    viewing: false
                }
            }));
            return;
        }
        
        // Show confirmation dialog
        setConfirmDialog({
            open: true,
            provider: provider,
            newValue: currentValue,
            fieldName: fieldName
        });
    };

    const handleConfirmSave = async () => {
        const { provider, newValue } = confirmDialog;
        
        try {
            setSaving(prev => ({ ...prev, [provider]: true }));
            setConfirmDialog({ open: false, provider: null, newValue: '', fieldName: '' });
            
            const payload = {};
            if (provider === 'openai') {
                payload.openai_api_key = newValue;
            } else if (provider === 'ollama') {
                payload.ollama_url = newValue;
            } else if (provider === 'mlx') {
                payload.mlx_url = newValue;
            } else if (provider === 'openrouter') {
                payload.openrouter_api_key = newValue;
            } else if (provider === 'googleaistudio') {
                payload.google_ai_studio_api_key = newValue;
            } else if (provider === 'groq') {
                payload.groq_api_key = newValue;
            } else if (provider === 'claude') {
                payload.claude_api_key = newValue;
            }

            await api.put('/admin/llm-config', payload);
            
            setSnackbar({
                open: true,
                message: `${provider.toUpperCase()} configuration saved successfully`,
                severity: 'success'
            });
            
            // Update original value and exit edit mode
            setConfigs(prev => ({
                ...prev,
                [provider]: {
                    ...prev[provider],
                    editing: false,
                    viewing: false,
                    originalValue: isUrlProvider(provider) ? prev[provider].url : prev[provider].apiKey
                }
            }));
            
            // Reload settings
            await fetchConfigs();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || `Failed to save ${provider} configuration`,
                severity: 'error'
            });
        } finally {
            setSaving(prev => ({ ...prev, [provider]: false }));
        }
    };

    const handleCancelConfirm = () => {
        setConfirmDialog({ open: false, provider: null, newValue: '', fieldName: '' });
    };

    const handleEdit = (provider) => {
        setConfigs(prev => ({
            ...prev,
            [provider]: { 
                ...prev[provider], 
                editing: true,
                showKey: true, // Show the key when editing
                viewing: false // Exit view mode if active
            }
        }));
    };

    const handleCancel = (provider) => {
        setConfigs(prev => {
            const originalValue = prev[provider].originalValue;
            return {
                ...prev,
                [provider]: {
                    ...prev[provider],
                    editing: false,
                    viewing: false,
                    apiKey: isUrlProvider(provider) ? prev[provider].apiKey : originalValue,
                    url: isUrlProvider(provider) ? originalValue : prev[provider].url
                }
            };
        });
    };

    const handleCopy = async (provider, fieldKey) => {
        const value = isUrlProvider(provider)
            ? configs[provider].url 
            : configs[provider].apiKey;
        
        if (!value) {
            setSnackbar({
                open: true,
                message: 'No value to copy',
                severity: 'warning'
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            setSnackbar({
                open: true,
                message: `${fieldKey === 'url' ? 'URL' : 'API Key'} copied to clipboard`,
                severity: 'success'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to copy to clipboard',
                severity: 'error'
            });
        }
    };

    const handleTestConnection = async (provider) => {
        try {
            setConfigs(prev => ({
                ...prev,
                [provider]: { ...prev[provider], testing: true, status: 'testing' }
            }));

            const response = await api.post(`/admin/llm-config/test/${provider}`);
            
            setConfigs(prev => ({
                ...prev,
                [provider]: {
                    ...prev[provider],
                    testing: false,
                    status: response.data.success ? 'connected' : 'failed'
                }
            }));

            setSnackbar({
                open: true,
                message: response.data.success 
                    ? `${provider.toUpperCase()} connection successful`
                    : `${provider.toUpperCase()} connection failed: ${response.data.message || 'Unknown error'}`,
                severity: response.data.success ? 'success' : 'error'
            });
        } catch (err) {
            setConfigs(prev => ({
                ...prev,
                [provider]: { ...prev[provider], testing: false, status: 'failed' }
            }));
            setSnackbar({
                open: true,
                message: `Connection test failed: ${err.response?.data?.detail || err.message}`,
                severity: 'error'
            });
        }
    };

    const toggleShowKey = (provider) => {
        setConfigs(prev => ({
            ...prev,
            [provider]: { ...prev[provider], showKey: !prev[provider].showKey }
        }));
    };

    const toggleView = (provider) => {
        setConfigs(prev => ({
            ...prev,
            [provider]: { ...prev[provider], viewing: !prev[provider].viewing }
        }));
    };

    const handleCheckCredits = async () => {
        setCreditsDialog({ open: true, loading: true, data: null, error: null });
        try {
            const response = await api.get('/openrouter/credits');
            setCreditsDialog({ open: true, loading: false, data: response.data, error: null });
        } catch (err) {
            const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch credits';
            setCreditsDialog({ open: true, loading: false, data: null, error: errorMessage });
        }
    };

    const handleCloseCreditsDialog = () => {
        setCreditsDialog({ open: false, loading: false, data: null, error: null });
    };

    const formatCreditsValue = (value) => {
        if (value === null || value === undefined) return 'N/A';
        if (typeof value === 'number') {
            return `$${value.toFixed(4)}`;
        }
        return String(value);
    };

    const getStatusChip = (status) => {
        const statusMap = {
            configured: { label: 'Configured', color: 'success', icon: <CheckCircle /> },
            not_configured: { label: 'Not Configured', color: 'default', icon: <ErrorIcon /> },
            connected: { label: 'Connected', color: 'success', icon: <CheckCircle /> },
            failed: { label: 'Failed', color: 'error', icon: <ErrorIcon /> },
            testing: { label: 'Testing...', color: 'info', icon: <CircularProgress size={16} /> },
            unknown: { label: 'Unknown', color: 'default', icon: null }
        };

        const statusInfo = statusMap[status] || statusMap.unknown;
        return (
            <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                color={statusInfo.color}
                size="small"
                sx={{ ml: 1 }}
            />
        );
    };

    const ProviderCard = ({ provider, title, description, icon, fields, extraActions }) => {
        const config = configs[provider];
        const isSaving = saving[provider];
        const hasValue = readOnly
            ? (config.status === 'configured')
            : (isUrlProvider(provider) ? config.url : config.apiKey);

        const getMaskedValue = () => {
            if (isUrlProvider(provider)) {
                return config.url ? '••••••••••••••••' : '';
            } else {
                return config.apiKey ? '••••••••••••••••' : '';
            }
        };

        // Get the appropriate logo for each provider
        const getProviderLogo = () => {
            switch (provider) {
                case 'ollama':
                    return ollamaIcon;
                case 'openai':
                    return chatGptIcon;
                case 'openrouter':
                    return openRouterIcon;
                case 'googleaistudio':
                    return googleAiIcon;
                case 'groq':
                    return groqIcon;
                default:
                    return null;
            }
        };

        const providerLogo = getProviderLogo();

        return (
            <Card 
                elevation={3}
                sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                    }
                }}
            >
                <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {providerLogo ? (
                            <Box
                                component="img"
                                src={providerLogo}
                                alt={title}
                                sx={{ 
                                    width: 40, 
                                    height: 40, 
                                    borderRadius: '8px', 
                                    objectFit: 'contain',
                                    mr: 1.5
                                }}
                            />
                        ) : (
                            icon
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="bold">
                                {title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {description}
                            </Typography>
                        </Box>
                        {getStatusChip(config.status)}
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />

                    {fields.map((field, idx) => (
                        <Box key={idx}>
                            {!config.editing ? (
                                <Box sx={{ mt: 2, mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {field.label}
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            backgroundColor: 'action.hover',
                                            display: 'flex',
                                            alignItems: config.viewing ? 'flex-start' : 'center',
                                            justifyContent: 'space-between',
                                            minHeight: 56,
                                            flexDirection: config.viewing ? 'column' : 'row',
                                            gap: config.viewing ? 1 : 0
                                        }}
                                    >
                                        <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0, pr: config.viewing ? 0 : 1 }}>
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    color: hasValue ? 'text.primary' : 'text.disabled',
                                                    letterSpacing: config.viewing ? 0.5 : 1,
                                                    wordBreak: config.viewing ? 'break-all' : 'normal',
                                                    overflowWrap: config.viewing ? 'anywhere' : 'normal',
                                                    whiteSpace: config.viewing ? 'pre-wrap' : 'nowrap',
                                                    overflow: config.viewing ? 'visible' : 'hidden',
                                                    textOverflow: config.viewing ? 'clip' : 'ellipsis',
                                                    maxWidth: '100%',
                                                    display: 'block',
                                                    lineHeight: config.viewing ? 1.6 : 1.5,
                                                    wordWrap: config.viewing ? 'break-word' : 'normal'
                                                }}
                                            >
                                                {hasValue 
                                                    ? (config.viewing 
                                                        ? (isUrlProvider(provider) ? config.url : config.apiKey)
                                                        : getMaskedValue())
                                                    : 'Not configured'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignSelf: config.viewing ? 'flex-end' : 'center' }}>
                                            {hasValue && (
                                                <>
                                                    <Tooltip title={readOnly ? "Admin only" : (config.viewing ? "Hide" : "View")}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleView(provider)}
                                                                disabled={readOnly}
                                                                sx={{
                                                                    border: '1px solid',
                                                                    borderColor: 'divider'
                                                                }}
                                                            >
                                                                {config.viewing ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={readOnly ? "Admin only" : "Copy to clipboard"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCopy(provider, field.key)}
                                                                disabled={readOnly}
                                                                sx={{
                                                                    border: '1px solid',
                                                                    borderColor: 'divider'
                                                                }}
                                                            >
                                                                <CopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            )}
                                            <Tooltip title={readOnly ? "Admin only" : "Edit"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(provider)}
                                                        disabled={readOnly}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    {field.helperText && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {field.helperText}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    label={field.label}
                                    type="text"
                                    value={config[field.key]}
                                    onChange={(e) => {
                                        setConfigs(prev => ({
                                            ...prev,
                                            [provider]: { ...prev[provider], [field.key]: e.target.value }
                                        }));
                                    }}
                                    margin="normal"
                                    variant="outlined"
                                    InputProps={{
                                        sx: {
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem'
                                        },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {config[field.key] && (
                                                        <Tooltip title="Copy to clipboard">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCopy(provider, field.key)}
                                                                edge="end"
                                                                sx={{ mr: 0.5 }}
                                                            >
                                                                <CopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </InputAdornment>
                                        )
                                    }}
                                    placeholder={field.placeholder}
                                    helperText={field.helperText}
                                />
                            )}
                        </Box>
                    ))}
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                    {!config.editing ? (
                        <>
                            {!readOnly && extraActions}
                            <Tooltip title={readOnly ? "Admin only" : "Test connection"}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={config.testing ? <CircularProgress size={16} /> : <RefreshIcon />}
                                        onClick={() => handleTestConnection(provider)}
                                        disabled={readOnly || config.testing || !hasValue}
                                        size="small"
                                    >
                                        Test
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={() => handleCancel(provider)}
                                disabled={isSaving}
                                size="small"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                                onClick={() => handleSave(provider)}
                                disabled={isSaving}
                                size="small"
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        </>
                    )}
                </CardActions>
            </Card>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LLMIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            LLM Configuration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {readOnly
                                ? 'View configured AI model providers (read-only)'
                                : 'Manage API keys and endpoints for AI model providers'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Local providers (Ollama, MLX) first, then the cloud providers, two per row */}
                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="ollama"
                        title="Ollama"
                        description="Local LLM models (Llama, Mistral, etc.)"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'secondary.main' }} />}
                        fields={[
                            {
                                key: 'url',
                                label: 'Base URL',
                                type: 'text',
                                placeholder: 'http://localhost:11434',
                                helperText: 'Ollama server URL (default: http://localhost:11434)'
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="mlx"
                        title="MLX (LM Studio)"
                        description="Local MLX models served by LM Studio (OpenAI-compatible API)"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'secondary.main' }} />}
                        fields={[
                            {
                                key: 'url',
                                label: 'Base URL',
                                type: 'text',
                                placeholder: 'http://localhost:1234/v1',
                                helperText: 'LM Studio server URL including /v1 (default: http://localhost:1234/v1)'
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="openai"
                        title="OpenAI"
                        description="GPT-4, GPT-3.5, and other OpenAI models"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
                        fields={[
                            {
                                key: 'apiKey',
                                label: 'API Key',
                                type: 'password',
                                placeholder: 'sk-...',
                                helperText: 'Your OpenAI API key from platform.openai.com'
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="openrouter"
                        title="OpenRouter"
                        description="Access to multiple AI models via single API"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'info.main' }} />}
                        fields={[
                            {
                                key: 'apiKey',
                                label: 'API Key',
                                type: 'password',
                                placeholder: 'sk-or-...',
                                helperText: 'Your OpenRouter API key from openrouter.ai'
                            }
                        ]}
                        extraActions={
                            <Tooltip title="Check API credits">
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CreditsIcon />}
                                        onClick={handleCheckCredits}
                                        disabled={!configs.openrouter.apiKey}
                                        size="small"
                                    >
                                        Credits
                                    </Button>
                                </span>
                            </Tooltip>
                        }
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="googleaistudio"
                        title="Google AI Studio"
                        description="Gemini models via Google AI Studio"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'success.main' }} />}
                        fields={[
                            {
                                key: 'apiKey',
                                label: 'API Key',
                                type: 'password',
                                placeholder: 'AIza...',
                                helperText: 'Your Google AI Studio API key from aistudio.google.com'
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="groq"
                        title="Groq"
                        description="Fast inference for Llama, Mixtral, and more"
                        icon={<LLMIcon sx={{ fontSize: 28, color: 'warning.main' }} />}
                        fields={[
                            {
                                key: 'apiKey',
                                label: 'API Key',
                                type: 'password',
                                placeholder: 'gsk_...',
                                helperText: 'Your Groq API key from console.groq.com'
                            }
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ProviderCard
                        provider="claude"
                        title="Claude (Anthropic)"
                        description="Anthropic's AI assistant - helpful, harmless, honest"
                        icon={<LLMIcon sx={{ fontSize: 28, color: '#D97706' }} />}
                        fields={[
                            {
                                key: 'apiKey',
                                label: 'API Key',
                                type: 'password',
                                placeholder: 'sk-ant-...',
                                helperText: 'Your Anthropic API key from console.anthropic.com'
                            }
                        ]}
                    />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={handleCancelConfirm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Confirm {confirmDialog.fieldName} Change
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Are you sure you want to update the {confirmDialog.fieldName.toLowerCase()} for {confirmDialog.provider?.toUpperCase()}?
                    </DialogContentText>
                    <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'action.hover', 
                        borderRadius: 1,
                        mb: 2
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            New {confirmDialog.fieldName}:
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                overflowWrap: 'anywhere',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {confirmDialog.newValue || '(empty)'}
                        </Typography>
                    </Box>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        This action will overwrite the existing configuration. Make sure the new {confirmDialog.fieldName.toLowerCase()} is correct.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelConfirm} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSave}
                        variant="contained"
                        color="primary"
                        disabled={saving[confirmDialog.provider]}
                        startIcon={saving[confirmDialog.provider] ? <CircularProgress size={16} /> : <SaveIcon />}
                    >
                        {saving[confirmDialog.provider] ? 'Saving...' : 'Confirm & Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* OpenRouter Credits Dialog */}
            <Dialog
                open={creditsDialog.open}
                onClose={handleCloseCreditsDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditsIcon color="info" />
                    OpenRouter API Credits
                </DialogTitle>
                <DialogContent>
                    {creditsDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Fetching credits information...</Typography>
                        </Box>
                    ) : creditsDialog.error ? (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {creditsDialog.error}
                        </Alert>
                    ) : creditsDialog.data ? (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            API Key Label
                                        </Typography>
                                        <Typography variant="h6">
                                            {creditsDialog.data.label || 'N/A'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Total Usage
                                        </Typography>
                                        <Typography variant="h6">
                                            {formatCreditsValue(creditsDialog.data.usage)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Credit Limit
                                        </Typography>
                                        <Typography variant="h6">
                                            {creditsDialog.data.limit !== null ? formatCreditsValue(creditsDialog.data.limit) : 'Unlimited'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Remaining
                                        </Typography>
                                        <Typography variant="h6" color={creditsDialog.data.limit_remaining !== null && creditsDialog.data.limit_remaining < 1 ? 'error.main' : 'success.main'}>
                                            {creditsDialog.data.limit_remaining !== null ? formatCreditsValue(creditsDialog.data.limit_remaining) : 'Unlimited'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Account Type
                                        </Typography>
                                        <Typography variant="h6">
                                            {creditsDialog.data.is_free_tier ? 'Free Tier' : 'Paid Account'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                            {creditsDialog.data.rate_limit && Object.keys(creditsDialog.data.rate_limit).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Rate Limits
                                    </Typography>
                                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(creditsDialog.data.rate_limit, null, 2)}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreditsDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LLMsManagement;
